import React, { useState, useEffect } from 'react';
import { normalizeScheduledDeliveryConfig, type ScheduledDeliveryConfig } from '../utils/scheduledDeliveryConfig';

export type { ScheduledDeliveryConfig } from '../utils/scheduledDeliveryConfig';

interface TimetableDaySchedule {
  periods: { start: string; end: string }[];
  non_business: boolean;
  is_24_7?: boolean;
}

interface TimetableData {
  weekly: Record<string, TimetableDaySchedule>;
  special_days: unknown[];
}

interface ScheduledDeliveryDialogProps {
  open: boolean;
  config: ScheduledDeliveryConfig | null;
  workingTimetable?: TimetableData | null;
  onSave: (config: ScheduledDeliveryConfig) => void;
  onCancel: () => void;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function getDefaultConfig(): ScheduledDeliveryConfig {
  const slots: Record<string, boolean> = {};
  for (let i = 0; i < 24; i++) {
    slots[`${i.toString().padStart(2, '0')}`] = i >= 8 && i < 22; // 08:00-22:00 active by default
  }
  return { slots, max_orders_per_slot: null, min_preorder_hours: 1 };
}

export const ScheduledDeliveryDialog: React.FC<ScheduledDeliveryDialogProps> = ({
  open,
  config,
  workingTimetable: _workingTimetable,
  onSave,
  onCancel,
}) => {
  void _workingTimetable; // reserved for future working hours validation
  const [localConfig, setLocalConfig] = useState<ScheduledDeliveryConfig>(
    normalizeScheduledDeliveryConfig(config) || getDefaultConfig()
  );

  useEffect(() => {
    setLocalConfig(normalizeScheduledDeliveryConfig(config) || getDefaultConfig());
  }, [config]);

  if (!open) return null;

  const toggleSlot = (hour: string) => {
    setLocalConfig(prev => ({
      ...prev,
      slots: { ...prev.slots, [hour]: !prev.slots[hour] },
    }));
  };

  const toggleAll = () => {
    const allActive = HOURS.every(h => localConfig.slots[h.toString().padStart(2, '0')]);
    const newSlots: Record<string, boolean> = {};
    for (const h of HOURS) {
      newSlots[h.toString().padStart(2, '0')] = !allActive;
    }
    setLocalConfig(prev => ({ ...prev, slots: newSlots }));
  };

  const activeCount = HOURS.filter(h => localConfig.slots[h.toString().padStart(2, '0')]).length;

  const handleSave = () => {
    onSave(localConfig);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Scheduled Delivery Settings</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>

        <div className="p-4 space-y-5">
          {/* Slot Grid */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700">
                Time Slots ({activeCount} active)
              </h4>
              <button
                type="button"
                onClick={toggleAll}
                className="text-xs text-alps-primary hover:text-alps-primary/80 font-medium"
              >
                {activeCount === 24 ? 'Deactivate all' : 'Activate all'}
              </button>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {HOURS.map(hour => {
                const hourStr = hour.toString().padStart(2, '0');
                const isActive = !!localConfig.slots[hourStr];
                const nextHour = ((hour + 1) % 24).toString().padStart(2, '0');
                return (
                  <button
                    key={hour}
                    type="button"
                    onClick={() => toggleSlot(hourStr)}
                    className={`
                      px-2 py-2 rounded-lg border text-xs font-medium transition-colors
                      ${isActive
                        ? 'bg-alps-primary/10 border-alps-primary text-alps-primary'
                        : 'bg-gray-50 border-gray-200 text-gray-400 hover:border-gray-300'
                      }
                    `}
                  >
                    {hourStr}:00–{nextHour}:00
                  </button>
                );
              })}
            </div>
          </div>

          {/* Max orders per slot */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max orders per slot
              <span className="text-gray-400 font-normal ml-1">(optional, blank = unlimited)</span>
            </label>
            <input
              type="number"
              min="1"
              value={localConfig.max_orders_per_slot ?? ''}
              onChange={(e) => {
                const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                setLocalConfig(prev => ({ ...prev, max_orders_per_slot: val }));
              }}
              placeholder="Unlimited"
              className="w-40 border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-alps-primary focus:border-alps-primary"
            />
          </div>

          {/* Min preorder time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum preorder time (hours)
              <span className="text-gray-400 font-normal ml-1">(how far in advance clients must order)</span>
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={localConfig.min_preorder_hours}
              onChange={(e) => {
                const parsed = parseInt(e.target.value, 10);
                const val = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
                setLocalConfig(prev => ({ ...prev, min_preorder_hours: val }));
              }}
              className="w-40 border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-alps-primary focus:border-alps-primary"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 p-4 border-t">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-alps-primary border border-transparent rounded-md hover:bg-alps-primary/90"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};
